import "@fontsource-variable/archivo";
import "@fontsource-variable/inter";
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowUpRight,
  Check,
  ChevronRight,
  FileText,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  Upload,
} from "lucide-react";
import type { AdminConsole, WorkspaceConsole } from "../shared/platform";
import { api } from "./api";
import "./styles.css";

type Route = {
  area: "public" | "client" | "staff" | "admin";
  page: string;
  workspaceId?: string;
};
const parseRoute = (): Route => {
  const value = location.hash.replace(/^#\/?/, "") || "public/home";
  const [area = "public", page = "home", workspaceId] = value.split("/");
  return { area: area as Route["area"], page, workspaceId };
};
const go = (to: string) => {
  location.hash = to;
};

const Seam = ({ dark = false }: { dark?: boolean }) => (
  <svg className="brand-mark" viewBox="0 0 100 100" aria-hidden="true">
    {!dark && <rect width="100" height="100" fill="#0E1217" />}
    <polygon
      points="58,0 66,0 54,33 60,62 46,100 38,100 52,62 46,33"
      fill="#C9A24B"
    />
  </svg>
);
const Brand = ({ onDark = false }: { onDark?: boolean }) => (
  <span className="brand">
    <Seam dark={onDark} />
    <span>DREAMERS REVENUE</span>
  </span>
);
const Status = ({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) => <span className={`status ${tone}`}>{children}</span>;
const money = (value?: number | null) =>
  value == null
    ? "Value not verified"
    : new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency: "AUD",
        maximumFractionDigits: 0,
      }).format(value / 100);
const date = (value?: string | null) =>
  value
    ? new Intl.DateTimeFormat("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(new Date(value))
    : "—";
const privateHome = (session: {
  platformAdmin: boolean;
  memberships: Array<{ workspaceId: string; role: string }>;
}) => {
  if (session.platformAdmin) return "admin/overview";
  const membership = session.memberships[0];
  if (!membership) return "public/login";
  return ["operator", "maker", "auditor"].includes(membership.role)
    ? `staff/overview/${membership.workspaceId}`
    : `client/overview/${membership.workspaceId}`;
};

function PublicNav() {
  const brief = () => {
    go("public/home");
    requestAnimationFrame(() =>
      document.getElementById("brief")?.scrollIntoView(),
    );
  };
  return (
    <header className="site-nav">
      <button className="brand-button" onClick={brief}>
        <Brand />
      </button>
      <nav aria-label="Public navigation">
        <button onClick={brief}>Brief</button>
        <button onClick={() => go("public/terms")}>Boundaries</button>
        <button onClick={() => go("public/help")}>Help</button>
      </nav>
      <button className="nav-access" onClick={() => go("public/login")}>
        Client & staff access <ArrowUpRight size={14} />
      </button>
    </header>
  );
}

function LegalPage({ kind }: { kind: "privacy" | "terms" | "help" }) {
  const copy =
    kind === "privacy"
      ? [
          "Privacy and data handling",
          "We only request the minimum commercial information needed to assess fit or deliver an agreed engagement. Selected records remain client-owned. We separate exclusions, suppression records and incomplete authority before a draft can be prepared.",
          "This surface is a plain-language service notice, not a substitute for the client data schedule or a professional privacy review.",
        ]
      : kind === "terms"
        ? [
            "Service boundaries",
            "Dreamers Revenue prepares evidence-ranked commercial work for named human review. It does not guarantee recovered revenue, send external communications at launch, perform debt collection, sign contracts, alter payout details or give legal, financial or regulated advice.",
            "Engagement terms, data schedules and release permissions are versioned inside the private workspace and require appropriate professional review before acceptance.",
          ]
        : [
            "Help and access",
            "A private review begins with the application. Once the engagement is accepted, the named client contact receives a controlled workspace and onboarding checklist. Every screen explains what is required next; blocked work stays visible rather than silently progressing.",
            "If a private link is expired, an invoice is missing, or a source file is rejected, use the workspace status record or contact the person who issued the invitation. Never email sensitive records or access tokens.",
          ];
  return (
    <>
      <PublicNav />
      <main className="policy-page">
        <button className="back-link" onClick={() => go("public/home")}>
          <ArrowLeft size={15} /> Back to brief
        </button>
        <p className="section-code">Dreamers Revenue / {kind}</p>
        <h1>{copy[0]}</h1>
        <p>{copy[1]}</p>
        <p>{copy[2]}</p>
        <section className="policy-callout">
          <LockKeyhole size={20} />
          <div>
            <b>Controlled access</b>
            <p>
              External delivery remains disabled until a separately reviewed
              connection and exact human approval exist.
            </p>
          </div>
        </section>
      </main>
    </>
  );
}

function Landing() {
  return (
    <>
      <PublicNav />
      <main id="brief" className="site-main">
        <section className="hero-premium">
          <div className="hero-index">
            <span>DW / 01</span>
            <span>Australia</span>
          </div>
          <div className="hero-copy">
            <p className="kicker">Special situations revenue operations</p>
            <h1>
              The deals are already there.
              <br /> <em>We find the next decision.</em>
            </h1>
            <p className="hero-intro">
              Dreamers Revenue reads the records your team already owns and
              reconstructs the decision history. Each recommended move carries
              its source and waits for a named approver.
            </p>
            <div className="hero-actions">
              <button
                className="primary-action"
                onClick={() => go("public/apply")}
              >
                Request a private review <ArrowUpRight size={15} />
              </button>
              <button
                className="text-action"
                onClick={() =>
                  document.getElementById("process")?.scrollIntoView()
                }
              >
                Read the 72-hour brief
              </button>
            </div>
          </div>
          <aside className="brief-card">
            <div className="brief-head">
              <span>Revenue Rescue Sprint</span>
              <b>Brief 01</b>
            </div>
            <dl>
              <div>
                <dt>Working window</dt>
                <dd>72 hours after accepted inputs</dd>
              </div>
              <div>
                <dt>Review set</dt>
                <dd>Up to 30 qualified records</dd>
              </div>
              <div>
                <dt>Source</dt>
                <dd>Client-owned pipeline only</dd>
              </div>
              <div>
                <dt>Release</dt>
                <dd>Named human approval</dd>
              </div>
            </dl>
          </aside>
        </section>
        <section className="premise">
          <p className="section-code">The premise</p>
          <p>
            A quiet proposal, a deferred budget and a missed handoff may sit in
            the same CRM stage. They are different commercial situations. We
            establish what is true and prepare the smallest credible next move.
          </p>
        </section>
        <section id="process" className="process-section">
          <div className="process-title">
            <p className="section-code">Operating brief / 72 hours</p>
            <h2>Fast work needs a visible mechanism.</h2>
            <p>
              The clock is a delivery frame, not a cash promise. Incomplete
              authority or unusable source material pauses it.
            </p>
          </div>
          <div className="process-ledger">
            {[
              [
                "0–4 hrs",
                "Intake and quarantine",
                "Confirm authority and separate exclusions.",
              ],
              [
                "By 24 hrs",
                "Cash Opportunity Map",
                "Rank eligible records with their source trail.",
              ],
              [
                "24–48 hrs",
                "Draft and audit",
                "Prepare each move and check it independently.",
              ],
              [
                "48–72 hrs",
                "Decision desk",
                "Deliver exact drafts, exceptions and next steps.",
              ],
            ].map(([time, title, body]) => (
              <article key={time}>
                <time>{time}</time>
                <h3>{title}</h3>
                <p>{body}</p>
                <span>→</span>
              </article>
            ))}
          </div>
        </section>
        <section className="investment-section">
          <div>
            <p className="section-code">Founding engagements</p>
            <h2>Start with the smallest paid decision.</h2>
            <p>All prices are Australian dollars and exclude GST.</p>
          </div>
          <div className="investment-ledger">
            <article>
              <span>01 / Diagnostic</span>
              <h3>Cash Opportunity Map</h3>
              <strong>A$2,500 + GST</strong>
              <p>
                A bounded review of the strongest qualified records, exclusions
                and exact next checks.
              </p>
            </article>
            <article className="featured">
              <span>02 / Execution</span>
              <h3>Revenue Rescue Sprint</h3>
              <strong>A$9,500 + GST</strong>
              <p>
                The 72-hour operating file, decision queue, audited drafts and
                fourteen-day follow-through.
              </p>
            </article>
            <article>
              <span>03 / Continuity</span>
              <h3>Managed Revenue Desk</h3>
              <strong>A$5,000 + GST / month</strong>
              <p>
                Ongoing administration after a completed sprint and separate
                scope approval.
              </p>
            </article>
          </div>
        </section>
        <section className="governance-section">
          <div className="governance-copy">
            <p className="section-code light">Governance / release control</p>
            <h2>A draft is not an action.</h2>
            <p>
              Commercial work moves through source, maker, independent audit and
              named approval. At launch, release is disabled.
            </p>
            <button
              className="inverse-action"
              onClick={() => go("public/terms")}
            >
              Read service boundaries <ArrowUpRight size={15} />
            </button>
          </div>
        </section>
        <section className="final-cta">
          <p className="section-code light">Private intake / Australia</p>
          <h2>Put the pipeline on the table.</h2>
          <p>
            We will tell you what can move, what needs proof and what should
            stay untouched.
          </p>
          <button
            className="inverse-action paper"
            onClick={() => go("public/apply")}
          >
            Request a private review <ArrowUpRight size={15} />
          </button>
        </section>
      </main>
      <footer className="site-footer">
        <Brand onDark />
        <p>
          Operated by Dreamers Media Pty Ltd · ABN 19 679 585 936 · GST
          registered
        </p>
        <div>
          <button onClick={() => go("public/privacy")}>Privacy</button>
          <button onClick={() => go("public/terms")}>Terms</button>
          <button onClick={() => go("public/help")}>Help</button>
        </div>
      </footer>
    </>
  );
}

function Application() {
  const [receipt, setReceipt] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [startedAt] = useState(Date.now());
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    try {
      const result = await api.apply({
        contactName: String(form.get("name")),
        contactEmail: String(form.get("email")),
        companyName: String(form.get("company")),
        website: String(form.get("website") || "") || undefined,
        pipelineSummary: String(form.get("context")),
        recordCount: Number(form.get("recordCount")),
        averageDealCents: Math.round(Number(form.get("averageDeal")) * 100),
        authorityConfirmed: form.get("authority") === "on",
        privacyAccepted: form.get("privacy") === "on",
        startedAt,
      });
      setReceipt(result.data.reference);
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Submission could not be recorded.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="intake-page">
      <header>
        <button onClick={() => go("public/home")}>← Dreamers Revenue</button>
        <span>Private review / 01</span>
      </header>
      <div className="intake-layout">
        <aside>
          <p className="section-code">Fit review</p>
          <h1>Tell us what is sitting in the pipeline.</h1>
          <p>
            This creates a private review record—not a contract, payment or
            authority to contact anyone.
          </p>
          <dl>
            <div>
              <dt>Initial scope</dt>
              <dd>Up to 30 qualified records</dd>
            </div>
            <div>
              <dt>Jurisdiction</dt>
              <dd>Australia only at launch</dd>
            </div>
          </dl>
        </aside>
        <section>
          {receipt ? (
            <div className="intake-confirmation">
              <span>Received</span>
              <h2>Your reference is {receipt}.</h2>
              <p>
                We assess fit before requesting records, proposing terms or
                discussing payment. No prospect has been contacted.
              </p>
              <button
                className="primary-action"
                onClick={() => go("public/help")}
              >
                What happens next
              </button>
            </div>
          ) : (
            <form onSubmit={submit}>
              <div className="form-intro">
                <span>Application</span>
                <p>Required fields must be complete.</p>
              </div>
              <label>
                Full name
                <input required name="name" autoComplete="name" />
              </label>
              <label>
                Work email
                <input
                  required
                  type="email"
                  name="email"
                  autoComplete="email"
                />
              </label>
              <label>
                Company
                <input required name="company" />
              </label>
              <label>
                Company website
                <input type="url" name="website" placeholder="https://" />
              </label>
              <label>
                Approximate records
                <input required type="number" min="1" name="recordCount" />
              </label>
              <label>
                Average deal value (A$)
                <input required type="number" min="1000" name="averageDeal" />
              </label>
              <label className="wide">
                What is currently stuck?
                <textarea required minLength={20} name="context" rows={5} />
              </label>
              <label className="consent wide">
                <input required type="checkbox" name="authority" />
                <span>
                  I can authorise access to selected first-party records and
                  identify a named approver.
                </span>
              </label>
              <label className="consent wide">
                <input required type="checkbox" name="privacy" />
                <span>
                  I agree that Dreamers Media Pty Ltd may use this information
                  to assess fit and respond.
                </span>
              </label>
              {error && <ErrorNotice message={error} />}
              <div className="form-submit wide">
                <button className="primary-action" disabled={busy}>
                  {busy ? "Submitting…" : "Submit for review"}
                  <ArrowUpRight size={15} />
                </button>
                <span>
                  No record access or prospect contact occurs from this form.
                </span>
              </div>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}

function TokenIntake({ token }: { token: string }) {
  const [summary, setSummary] = useState<{
    reference: string;
    companyName: string;
    expiresAt: string;
  } | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [authority, setAuthority] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .clientIntake(token)
      .then((response) => setSummary(response.data))
      .catch((cause) =>
        setError(
          cause instanceof Error
            ? cause.message
            : "This secure link is unavailable.",
        ),
      );
  }, [token]);
  const submit = async () => {
    if (!file || !authority) return;
    setBusy(true);
    setError("");
    try {
      const raw = await file.text();
      const [header, ...lines] = raw.trim().split(/\r?\n/);
      const keys = header.split(",").map((key) => key.trim());
      const rows = lines
        .filter(Boolean)
        .map((line) =>
          Object.fromEntries(
            keys.map((key, index) => [
              key,
              line.split(",")[index]?.trim() || "",
            ]),
          ),
        );
      const hash = Array.from(
        new Uint8Array(
          await crypto.subtle.digest("SHA-256", await file.arrayBuffer()),
        ),
      )
        .map((item) => item.toString(16).padStart(2, "0"))
        .join("");
      const response = await api.submitClientRecords(token, {
        filename: file.name,
        sha256: hash,
        rows,
        attestations: {
          authority: true,
          suppressionComplete: true,
          firstPartyOnly: true,
        },
      });
      setResult(
        `${response.data.acceptedCount} accepted · ${response.data.quarantinedCount} quarantined`,
      );
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The intake could not be recorded.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="intake-page">
      <header>
        <button onClick={() => go("public/home")}>← Dreamers Revenue</button>
        <span>Secure client intake</span>
      </header>
      <div className="intake-layout">
        <aside>
          <p className="section-code">Controlled transfer</p>
          <h1>{summary?.companyName || "Verifying secure link"}</h1>
          <p>
            {summary
              ? `Reference ${summary.reference}. This link expires ${date(summary.expiresAt)}.`
              : "No file is transferred while the link is being verified."}
          </p>
        </aside>
        <section>
          <div className="form-intro">
            <span>Selected commercial records</span>
            <p>
              Use the exact approved CSV format. Remove credentials, sensitive
              personal information, disputed debts and known opt-outs.
            </p>
          </div>
          {error && <ErrorNotice message={error} />}{" "}
          {result ? (
            <div className="intake-confirmation">
              <span>Receipt recorded</span>
              <h2>{result}</h2>
              <p>
                Dreamers Revenue will review source, authority and exclusions
                before any draft is prepared.
              </p>
            </div>
          ) : (
            <>
              <label className="document-drop">
                <Upload size={20} />
                <b>{file?.name || "Choose approved CSV"}</b>
                <small>
                  Nothing transfers until you choose Record selected records.
                </small>
                <input
                  type="file"
                  accept=".csv,text/csv"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
              </label>
              <label className="consent">
                <input
                  type="checkbox"
                  checked={authority}
                  onChange={(event) => setAuthority(event.target.checked)}
                />
                <span>
                  I am authorised to provide these selected first-party business
                  records, including known suppression and exclusion records.
                </span>
              </label>
              <button
                className="primary-action"
                disabled={!file || !authority || busy || !summary}
                onClick={submit}
              >
                {busy ? "Recording…" : "Record selected records"}
              </button>
            </>
          )}
        </section>
      </div>
    </main>
  );
}

function Login() {
  const [mode, setMode] = useState<"token" | "invitation">("token");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const value = String(new FormData(event.currentTarget).get("credential"));
    try {
      if (mode === "token") await api.login(value);
      else await api.invitationSession(value);
      const session = await api.session();
      go(privateHome(session.data));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Access could not be verified.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="desk-login">
      <div className="login-brand">
        <Brand onDark />
        <span>Private operating access</span>
      </div>
      <section>
        <button className="back-link" onClick={() => go("public/home")}>
          ← Return to public brief
        </button>
        <p className="section-code">Secure access</p>
        <h1>Enter your workspace.</h1>
        <p>
          Use the access token or invitation issued to you. Tokens and
          invitations never appear in shared reports or emails.
        </p>
        <div className="mode-tabs">
          <button
            type="button"
            className={mode === "token" ? "active" : ""}
            onClick={() => setMode("token")}
          >
            Access token
          </button>
          <button
            type="button"
            className={mode === "invitation" ? "active" : ""}
            onClick={() => setMode("invitation")}
          >
            Invitation
          </button>
        </div>
        <form onSubmit={submit}>
          <input
            className="sr-only"
            name="username"
            autoComplete="username"
            value="dreamers-revenue-access"
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
          <label>
            {mode === "token" ? "Access token" : "Invitation token"}
            <input
              required
              type="password"
              name="credential"
              autoComplete="current-password"
            />
          </label>
          {error && <ErrorNotice message={error} />}
          <button className="primary-action" disabled={busy}>
            {busy ? "Checking…" : "Continue"}
          </button>
        </form>
      </section>
    </main>
  );
}

function InvitationAccess({ token }: { token: string }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const accept = async () => {
    setBusy(true);
    setError("");
    try {
      await api.invitationSession(token);
      const session = await api.session();
      if (!session.data.memberships[0])
        throw new Error(
          "The invitation did not create a workspace membership.",
        );
      go(privateHome(session.data));
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "This invitation could not be accepted.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="desk-login">
      <div className="login-brand">
        <Brand onDark />
        <span>Individual workspace invitation</span>
      </div>
      <section>
        <button className="back-link" onClick={() => go("public/home")}>
          ← Return to public brief
        </button>
        <p className="section-code">Secure invitation</p>
        <h1>Accept your named access.</h1>
        <p>
          This invitation is single-use, expires automatically and grants only
          its recorded workspace role. Accepting it creates your own revocable
          session.
        </p>
        {error && <ErrorNotice message={error} />}
        <button className="primary-action" onClick={accept} disabled={busy}>
          {busy ? "Accepting…" : "Accept invitation"}
        </button>
      </section>
    </main>
  );
}

function ErrorNotice({
  message,
  retry,
}: {
  message: string;
  retry?: () => void;
}) {
  return (
    <p className="form-error">
      <AlertTriangle size={16} />
      <span>{message}</span>
      {retry && <button onClick={retry}>Retry</button>}
    </p>
  );
}
function Empty({
  title,
  children,
  action,
}: {
  title: string;
  children: ReactNode;
  action?: ReactNode;
}) {
  return (
    <section className="empty-state">
      <p className="section-code">No record yet</p>
      <h2>{title}</h2>
      <p>{children}</p>
      {action}
    </section>
  );
}
function Blocked({ reason }: { reason: string }) {
  return (
    <section className="blocked-state">
      <LockKeyhole size={18} />
      <div>
        <b>Held for a human prerequisite</b>
        <p>{reason}</p>
      </div>
    </section>
  );
}

const clientNav = [
  ["overview", "Overview"],
  ["onboarding", "Start here"],
  ["records", "Records & documents"],
  ["opportunities", "Opportunity map"],
  ["decisions", "Decisions"],
  ["communications", "Communications"],
  ["reports", "Reports"],
  ["account", "Account & settings"],
];
const staffNav = [
  ["overview", "Portfolio"],
  ["applications", "Applications & commercial"],
  ["workspace", "Tenant workspace"],
  ["intake", "Intake & exceptions"],
  ["queue", "Operator queue"],
  ["drafts", "Maker drafting"],
  ["audit", "Auditor docket"],
  ["communications", "Communications"],
  ["jobs", "Job runs"],
  ["reports", "Reports"],
  ["templates", "Templates"],
  ["integrations", "Integrations"],
  ["audit-log", "Audit log"],
  ["settings", "Settings"],
];
const adminNav = [
  ["overview", "Control room"],
  ["tenants", "Tenants"],
  ["users", "Users & invitations"],
  ["jobs", "Job fleet"],
  ["integrations", "Integration health"],
  ["templates", "Templates"],
  ["retention", "Retention queue"],
  ["settings", "System controls"],
];

function Shell({
  children,
  route,
  console,
  admin,
  refresh,
}: {
  children: ReactNode;
  route: Route;
  console?: WorkspaceConsole;
  admin?: AdminConsole;
  refresh: () => void;
}) {
  const nav =
    route.area === "admin"
      ? adminNav
      : route.area === "staff"
        ? staffNav
        : clientNav;
  const workspaceId = console?.workspace.id || route.workspaceId || "";
  const role = console?.role || (admin ? "platform admin" : "member");
  const destination = (page: string) =>
    `${route.area}/${page}${workspaceId ? `/${workspaceId}` : ""}`;
  return (
    <div className="app-shell">
      <aside className="app-sidebar">
        <button
          className="brand-button sidebar-brand"
          onClick={() => go("public/home")}
        >
          <Brand />
        </button>
        <div className="workspace-label">
          <span>
            {route.area === "admin"
              ? "Platform administration"
              : console?.workspace.name || "Workspace"}
          </span>
          <b>{role}</b>
        </div>
        <nav aria-label="Workspace navigation">
          {nav.map(([page, label]) => (
            <button
              key={page}
              className={route.page === page ? "active" : ""}
              onClick={() => go(destination(page))}
            >
              {label}
              <ChevronRight size={13} />
            </button>
          ))}
        </nav>
        <footer>
          <button onClick={refresh}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button
            onClick={async () => {
              await api.logout();
              go("public/home");
            }}
          >
            End session
          </button>
        </footer>
      </aside>
      <main className="app-content">
        <header className="app-topbar">
          <div>
            <span>
              {console?.workspace.lifecycle || "Controlled workspace"}
            </span>
            <b>
              {console?.workspace.executionEnabled
                ? "Release configured"
                : "Release disabled"}
            </b>
          </div>
          <button onClick={refresh}>
            <RefreshCw size={14} /> Refresh record
          </button>
        </header>
        {children}
      </main>
    </div>
  );
}

function CommandButton({
  workspaceId,
  command,
  input = {},
  children,
  admin = false,
  className = "",
}: {
  workspaceId?: string;
  command: string;
  input?: Record<string, unknown>;
  children: ReactNode;
  admin?: boolean;
  className?: string;
}) {
  const [state, setState] = useState<"idle" | "busy" | "done" | "error">(
    "idle",
  );
  const [message, setMessage] = useState("");
  const run = async () => {
    setState("busy");
    setMessage("");
    try {
      await (admin
        ? api.adminCommand(command, input)
        : api.workspaceCommand(workspaceId || "", command, input));
      setState("done");
      setMessage(
        "Recorded. Refresh the workspace record to confirm the new state.",
      );
    } catch (cause) {
      setState("error");
      setMessage(
        cause instanceof Error
          ? cause.message
          : "This action could not be recorded.",
      );
    }
  };
  return (
    <span className="command-action">
      <button
        className={className || "action-button"}
        disabled={state === "busy"}
        onClick={run}
      >
        {state === "busy" ? "Recording…" : children}
      </button>
      {message && <small className={state}>{message}</small>}
    </span>
  );
}

function Overview({ data }: { data: WorkspaceConsole }) {
  const engagement = data.engagement;
  const required = data.onboarding.filter((item) => item.required);
  const complete = required.filter(
    (item) => item.status === "accepted" || item.status === "not_applicable",
  ).length;
  return (
    <Page title="Operating position" eyebrow="Workspace overview">
      <section className="ledger-strip">
        <article>
          <span>Service state</span>
          <strong>{engagement?.serviceState || "Not contracted"}</strong>
          <small>
            {engagement?.paymentVerification === "provider_verified"
              ? "Payment verified"
              : "Commercial record requires verification"}
          </small>
        </article>
        <article>
          <span>72-hour clock</span>
          <strong>
            {engagement?.startedAt
              ? `Due ${date(engagement.dueAt)}`
              : "Not started"}
          </strong>
          <small>
            {engagement?.startedAt
              ? "Accepted inputs and service state control the clock"
              : "Onboarding must be accepted"}
          </small>
        </article>
        <article>
          <span>Release control</span>
          <strong>{data.release.ready ? "Ready for approval" : "Held"}</strong>
          <small>
            {data.release.blockers.join(" · ") ||
              "No release action is enabled"}
          </small>
        </article>
      </section>
      <section className="two-column">
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Start here</p>
              <h2>Onboarding record</h2>
            </div>
            <button
              onClick={() => go(`client/onboarding/${data.workspace.id}`)}
            >
              Open checklist
            </button>
          </header>
          {required.length ? (
            <ol className="checklist">
              {required.slice(0, 5).map((item) => (
                <li key={item.id}>
                  <Status
                    tone={
                      item.status === "accepted"
                        ? "success"
                        : item.status === "blocked"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {item.status.replaceAll("_", " ")}
                  </Status>
                  <div>
                    <b>{item.title}</b>
                    <small>{item.description}</small>
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <Empty title="Onboarding has not been prepared">
              Dreamers Revenue will add the named responsibilities before the
              service clock can begin.
            </Empty>
          )}
          <footer>
            {complete} of {required.length} required items accepted
          </footer>
        </section>
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Next decision</p>
              <h2>Approval and exceptions</h2>
            </div>
            <button onClick={() => go(`client/decisions/${data.workspace.id}`)}>
              Open decisions
            </button>
          </header>
          {data.actions.filter((item) =>
            ["audited", "approved"].includes(item.state),
          ).length ? (
            data.actions
              .filter((item) => ["audited", "approved"].includes(item.state))
              .map((item) => (
                <article className="record-line" key={item.id}>
                  <div>
                    <b>{item.subject || item.channel}</b>
                    <small>
                      {item.companyName} · {item.state}
                    </small>
                  </div>
                  <Status>{item.state}</Status>
                </article>
              ))
          ) : (
            <Empty title="No decision is waiting">
              A draft must be sourced and independently audited before a named
              approver receives it.
            </Empty>
          )}
        </section>
      </section>
    </Page>
  );
}

function Onboarding({ data }: { data: WorkspaceConsole }) {
  const canSubmit =
    data.session.platformAdmin || ["owner", "operator"].includes(data.role);
  return (
    <Page title="Start here" eyebrow="Saved onboarding and service clock">
      <section className="two-column">
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Responsibilities</p>
              <h2>Every required input remains visible.</h2>
            </div>
          </header>
          {data.onboarding.length ? (
            <ol className="onboarding-list">
              {data.onboarding.map((item) => (
                <li key={item.id}>
                  <Status
                    tone={
                      item.status === "accepted"
                        ? "success"
                        : item.status === "blocked"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {item.status.replaceAll("_", " ")}
                  </Status>
                  <div>
                    <b>{item.title}</b>
                    <p>{item.description}</p>
                    <small>
                      Owner:{" "}
                      {item.ownerRole.replace(
                        "dreamers_revenue",
                        "Dreamers Revenue",
                      )}{" "}
                      · Due {date(item.dueAt)}
                    </small>
                    {item.blockedReason && (
                      <Blocked reason={item.blockedReason} />
                    )}
                  </div>
                  {canSubmit &&
                    ["not_started", "in_progress", "blocked"].includes(
                      item.status,
                    ) && (
                      <CommandButton
                        workspaceId={data.workspace.id}
                        command="onboarding.mark-submitted"
                        input={{ onboardingItemId: item.id }}
                      >
                        Mark submitted
                      </CommandButton>
                    )}
                </li>
              ))}
            </ol>
          ) : (
            <Empty title="No onboarding checklist yet">
              This is intentionally blocked until the engagement and named
              responsibilities are created.
            </Empty>
          )}
        </section>
        <section className="section-panel">
          <p className="section-code">Agreement record</p>
          <h2>Scope and data terms</h2>
          {data.agreements.length ? (
            data.agreements.map((agreement) => (
              <article className="record-line" key={agreement.id}>
                <div>
                  <b>{agreement.title}</b>
                  <small>
                    Version {agreement.version} · hash {agreement.contentHash}
                  </small>
                </div>
                <Status>{agreement.status}</Status>
                {data.role === "owner" &&
                  agreement.status === "issued" &&
                  agreement.professionalReviewStatus === "approved" && (
                    <CommandButton
                      workspaceId={data.workspace.id}
                      command="agreement.accept"
                      input={{ agreementId: agreement.id }}
                    >
                      Accept exact version
                    </CommandButton>
                  )}
              </article>
            ))
          ) : (
            <Empty title="Agreement drafts are being prepared">
              No client acceptance is requested until the professional-review
              status and exact version are recorded.
            </Empty>
          )}
          <Blocked reason="The delivery clock cannot start until all required inputs and three issued agreement versions are accepted." />
        </section>
      </section>
    </Page>
  );
}

function Records({ data }: { data: WorkspaceConsole }) {
  const [file, setFile] = useState<File | null>(null);
  const [message, setMessage] = useState("");
  const canUpload =
    data.session.platformAdmin ||
    ["owner", "operator", "maker"].includes(data.role);
  const canAccept =
    data.session.platformAdmin || ["owner", "operator"].includes(data.role);
  const upload = async () => {
    if (!file) return;
    setMessage("Uploading securely…");
    try {
      await api.uploadDocument(data.workspace.id, file, "source");
      setMessage("Document recorded. Refresh to see its validation state.");
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Upload failed.");
    }
  };
  return (
    <Page title="Records and documents" eyebrow="Private source register">
      <section className="two-column">
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Documents</p>
              <h2>Source material</h2>
            </div>
          </header>
          {canUpload ? (
            <>
              <label className="document-drop">
                <Upload size={20} />
                <b>{file?.name || "Choose a source document"}</b>
                <small>
                  Files are recorded against this workspace and reviewed before
                  they are used.
                </small>
                <input
                  type="file"
                  onChange={(event) => setFile(event.target.files?.[0] || null)}
                />
              </label>
              {file && (
                <button className="action-button" onClick={upload}>
                  Record document
                </button>
              )}
            </>
          ) : (
            <Blocked reason="Your viewer role can inspect accepted records but cannot upload or accept source material." />
          )}
          {message && <p className="inline-notice">{message}</p>}
          <div className="record-table">
            {data.documents.length ? (
              data.documents.map((item) => (
                <article key={item.id}>
                  <div>
                    <b>{item.filename}</b>
                    <small>
                      {item.category} · {item.byteSize.toLocaleString()} bytes ·{" "}
                      {date(item.createdAt)}
                    </small>
                  </div>
                  <Status
                    tone={
                      item.status === "accepted"
                        ? "success"
                        : item.status === "quarantined"
                          ? "danger"
                          : "neutral"
                    }
                  >
                    {item.status}
                  </Status>
                  {canAccept &&
                    ["pending", "quarantined"].includes(item.status) && (
                      <CommandButton
                        workspaceId={data.workspace.id}
                        command="document.accept"
                        input={{ documentId: item.id }}
                      >
                        Accept after review
                      </CommandButton>
                    )}
                </article>
              ))
            ) : (
              <Empty title="No documents have been recorded">
                Upload only approved commercial source material. Do not include
                credentials, payment cards, government identifiers or disputed
                debt files.
              </Empty>
            )}
          </div>
        </section>
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Import history</p>
              <h2>Pipeline exports</h2>
            </div>
          </header>
          {data.imports.length ? (
            data.imports.map((item) => (
              <article className="record-line" key={item.id}>
                <div>
                  <b>{item.filename}</b>
                  <small>
                    {item.rowCount} rows · {item.acceptedCount} accepted ·{" "}
                    {item.quarantinedCount} quarantined · {date(item.createdAt)}
                  </small>
                </div>
                <Status
                  tone={
                    item.status === "accepted"
                      ? "success"
                      : item.status === "quarantined"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {item.status}
                </Status>
              </article>
            ))
          ) : (
            <Empty title="No pipeline export yet">
              Use the controlled intake link issued after commercial and
              onboarding prerequisites are accepted.
            </Empty>
          )}
        </section>
      </section>
    </Page>
  );
}

function Opportunities({ data }: { data: WorkspaceConsole }) {
  const [selected, setSelected] = useState<string | null>(null);
  const item = data.opportunities.find(
    (opportunity) => opportunity.id === selected,
  );
  const canOperate =
    data.session.platformAdmin || ["owner", "operator"].includes(data.role);
  return (
    <Page title="Opportunity map" eyebrow="Evidence-ranked records">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Working register</p>
            <h2>Closest to a decision</h2>
          </div>
          <span>{data.opportunities.length} records</span>
        </header>
        {data.opportunities.length ? (
          <div className="opportunity-table">
            {data.opportunities.map((opportunity) => (
              <button
                key={opportunity.id}
                className="opportunity-row"
                onClick={() => setSelected(opportunity.id)}
              >
                <span>
                  <b>{opportunity.companyName}</b>
                  <small>
                    {opportunity.title} · evidence {opportunity.evidenceCount}
                  </small>
                </span>
                <strong>{money(opportunity.amountCents)}</strong>
                <Status
                  tone={
                    opportunity.stage === "quarantined" ? "danger" : "neutral"
                  }
                >
                  {opportunity.stage}
                </Status>
              </button>
            ))}
          </div>
        ) : (
          <Empty title="No opportunity has been accepted">
            Imports remain quarantined until source, authority and suppression
            checks are complete.
          </Empty>
        )}
      </section>
      {item && (
        <section className="detail-sheet" aria-live="polite">
          <button className="back-link" onClick={() => setSelected(null)}>
            Close detail
          </button>
          <p className="section-code">Evidence record</p>
          <h2>{item.companyName}</h2>
          <strong>{money(item.amountCents)}</strong>
          <dl>
            <div>
              <dt>Stage</dt>
              <dd>{item.stage}</dd>
            </div>
            <div>
              <dt>Evidence</dt>
              <dd>{item.evidenceCount} source record(s)</dd>
            </div>
            <div>
              <dt>Next action</dt>
              <dd>{item.nextAction || "Not assigned"}</dd>
            </div>
            <div>
              <dt>Due</dt>
              <dd>{date(item.nextActionDueAt)}</dd>
            </div>
          </dl>
          {item.exclusionReason && <Blocked reason={item.exclusionReason} />}
          <CommandButton
            workspaceId={data.workspace.id}
            command="opportunity.request-verification"
            input={{ opportunityId: item.id }}
          >
            Request evidence verification
          </CommandButton>
          {canOperate && (
            <OpportunityUpdate
              workspaceId={data.workspace.id}
              opportunity={item}
            />
          )}
        </section>
      )}
    </Page>
  );
}

function OpportunityUpdate({
  workspaceId,
  opportunity,
}: {
  workspaceId: string;
  opportunity: WorkspaceConsole["opportunities"][number];
}) {
  const [stage, setStage] = useState(opportunity.stage);
  const [nextAction, setNextAction] = useState(opportunity.nextAction || "");
  const [due, setDue] = useState(
    opportunity.nextActionDueAt?.slice(0, 16) || "",
  );
  return (
    <section className="inline-form">
      <h3>Update operating position</h3>
      <label>
        Stage
        <select
          value={stage}
          onChange={(event) => setStage(event.target.value)}
        >
          {[
            "quarantined",
            "ranked",
            "drafted",
            "audited",
            "approved",
            "simulated",
            "sent",
            "replied",
            "meeting",
            "proposal",
            "payment",
            "won",
            "lost",
            "nurture",
          ].map((value) => (
            <option key={value}>{value}</option>
          ))}
        </select>
      </label>
      <label>
        Next action
        <input
          value={nextAction}
          onChange={(event) => setNextAction(event.target.value)}
        />
      </label>
      <label>
        Due
        <input
          type="datetime-local"
          value={due}
          onChange={(event) => setDue(event.target.value)}
        />
      </label>
      <CommandButton
        workspaceId={workspaceId}
        command="opportunity.update"
        input={{
          opportunityId: opportunity.id,
          stage,
          nextAction,
          nextActionDueAt: due ? new Date(due).toISOString() : "",
        }}
      >
        Save operating position
      </CommandButton>
    </section>
  );
}

function Decisions({ data }: { data: WorkspaceConsole }) {
  const canApprove = data.role === "owner";
  const canRequestAudit = ["owner", "operator", "maker"].includes(data.role);
  const canRequestChange = ["owner", "auditor"].includes(data.role);
  return (
    <Page title="Decisions" eyebrow="Exact payloads, named approval">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Approval docket</p>
            <h2>A draft is not an action.</h2>
          </div>
        </header>
        {data.actions.length ? (
          data.actions.map((action) => (
            <article className="decision-record" key={action.id}>
              <div>
                <Status
                  tone={
                    action.state === "approved"
                      ? "success"
                      : action.state === "audit_failed"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {action.state}
                </Status>
                <h3>{action.subject || action.channel}</h3>
                <p>
                  {action.companyName} · {action.recipient}
                </p>
                <blockquote>{action.body}</blockquote>
                <small>
                  Content hash:{" "}
                  {action.contentHash || "Created when payload is finalised"}
                </small>
              </div>
              <div className="decision-actions">
                {canApprove && action.state === "audited" && (
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="action.approve"
                    input={{ actionId: action.id }}
                  >
                    Approve exact draft
                  </CommandButton>
                )}
                {canRequestAudit &&
                  ["draft", "audit_failed"].includes(action.state) && (
                    <CommandButton
                      workspaceId={data.workspace.id}
                      command="action.request-audit"
                      input={{ actionId: action.id }}
                    >
                      Request audit
                    </CommandButton>
                  )}
                {canRequestChange && (
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="action.request-change"
                    input={{ actionId: action.id }}
                  >
                    Request change
                  </CommandButton>
                )}
              </div>
            </article>
          ))
        ) : (
          <Empty title="No draft is ready for a decision">
            The maker prepares a source-bound draft and an independent auditor
            checks it before it enters this docket.
          </Empty>
        )}
        <Blocked
          reason={
            data.release.blockers.join(" · ") ||
            "External release is disabled by service policy."
          }
        />
      </section>
    </Page>
  );
}

function Communications({ data }: { data: WorkspaceConsole }) {
  const canOperate =
    data.session.platformAdmin ||
    ["owner", "operator", "maker", "auditor"].includes(data.role);
  const canVerify =
    data.session.platformAdmin || ["owner", "auditor"].includes(data.role);
  return (
    <Page
      title="Communications"
      eyebrow="Threads, outcomes and controlled delivery"
    >
      {canOperate && (
        <section className="two-column">
          <CommunicationRecorder data={data} />
          <OutcomeRecorder data={data} verified={canVerify} />
        </section>
      )}
      <section className="two-column">
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Threads</p>
              <h2>Commercial conversations</h2>
            </div>
          </header>
          {data.threads.length ? (
            data.threads.map((thread) => (
              <article className="record-line" key={thread.id}>
                <div>
                  <b>{thread.subject}</b>
                  <small>
                    {thread.channel} · {thread.direction} ·{" "}
                    {thread.classification || "unclassified"} · follow-up{" "}
                    {date(thread.nextFollowUpAt)}
                  </small>
                </div>
                <Status
                  tone={thread.status === "blocked" ? "danger" : "neutral"}
                >
                  {thread.status.replaceAll("_", " ")}
                </Status>
                {canVerify && thread.status !== "closed" && (
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="thread.classify"
                    input={{
                      threadId: thread.id,
                      classification: thread.classification || "reviewed",
                      status: "closed",
                    }}
                  >
                    Close reviewed thread
                  </CommandButton>
                )}
              </article>
            ))
          ) : (
            <Empty title="No communication thread exists">
              A thread is created from an approved draft or a verified inbound
              record. It is not invented from a prospect list.
            </Empty>
          )}
        </section>
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Notifications</p>
              <h2>Delivery register</h2>
            </div>
          </header>
          {data.notifications.length ? (
            data.notifications.map((note) => (
              <article className="record-line" key={note.id}>
                <div>
                  <b>{note.subject}</b>
                  <small>
                    {note.audience} · {note.channel} · {date(note.createdAt)}
                  </small>
                </div>
                <Status
                  tone={
                    note.status === "failed" || note.status === "blocked"
                      ? "danger"
                      : note.status === "sent"
                        ? "success"
                        : "neutral"
                  }
                >
                  {note.status}
                </Status>
                {note.status !== "read" && (
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="notification.read"
                    input={{ notificationId: note.id }}
                  >
                    Mark read
                  </CommandButton>
                )}
                {["blocked", "failed"].includes(note.status) &&
                  ["owner", "operator"].includes(data.role) && (
                    <CommandButton
                      workspaceId={data.workspace.id}
                      command="notification.retry"
                      input={{ notificationId: note.id }}
                    >
                      Retry safely
                    </CommandButton>
                  )}
              </article>
            ))
          ) : (
            <Empty title="No notification has been queued">
              Email is a disconnected provider until the owner completes the
              connection and monitored-inbox check.
            </Empty>
          )}
          <Blocked reason="No external communication is sent from this workspace while the provider and release policy are disconnected." />
        </section>
      </section>
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Verified result register</p>
            <h2>Cash and movement</h2>
          </div>
        </header>
        {data.outcomes.length ? (
          data.outcomes.map((outcome) => (
            <article className="record-line" key={outcome.id}>
              <div>
                <b>{outcome.outcomeType}</b>
                <small>
                  {outcome.evidence} · {date(outcome.occurredAt)}
                </small>
              </div>
              <strong>
                {outcome.amountCents ? money(outcome.amountCents) : "—"}
              </strong>
              <Status tone={outcome.verified ? "success" : "neutral"}>
                {outcome.verified ? "verified" : "awaiting verification"}
              </Status>
            </article>
          ))
        ) : (
          <Empty title="No outcome is recorded">
            Replies, meetings, proposals, invoices and payments count only when
            a source record is attached.
          </Empty>
        )}
      </section>
    </Page>
  );
}

function CommunicationRecorder({ data }: { data: WorkspaceConsole }) {
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [direction, setDirection] = useState("internal");
  const [opportunityId, setOpportunityId] = useState("");
  return (
    <section className="section-panel inline-form">
      <p className="section-code">Record communication</p>
      <h2>Capture the actual conversation</h2>
      <label>
        Opportunity
        <select
          value={opportunityId}
          onChange={(event) => setOpportunityId(event.target.value)}
        >
          <option value="">Workspace-level</option>
          {data.opportunities.map((item) => (
            <option key={item.id} value={item.id}>
              {item.companyName} — {item.title}
            </option>
          ))}
        </select>
      </label>
      <label>
        Direction
        <select
          value={direction}
          onChange={(event) => setDirection(event.target.value)}
        >
          <option value="internal">Internal note</option>
          <option value="inbound">Verified inbound</option>
          <option value="outbound">Outbound draft</option>
        </select>
      </label>
      <label>
        Subject
        <input
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
        />
      </label>
      <label>
        Source-based note
        <textarea
          rows={4}
          value={body}
          onChange={(event) => setBody(event.target.value)}
        />
      </label>
      <CommandButton
        workspaceId={data.workspace.id}
        command="thread.create"
        input={{
          opportunityId,
          direction,
          channel: direction === "internal" ? "internal" : "email",
          subject,
          body,
          classification:
            direction === "inbound" ? "inbound recorded" : "operating note",
        }}
      >
        Record without external delivery
      </CommandButton>
    </section>
  );
}

function OutcomeRecorder({
  data,
  verified,
}: {
  data: WorkspaceConsole;
  verified: boolean;
}) {
  const [opportunityId, setOpportunityId] = useState(
    data.opportunities[0]?.id || "",
  );
  const [outcomeType, setOutcomeType] = useState("reply");
  const [evidence, setEvidence] = useState("");
  const [amount, setAmount] = useState("");
  return (
    <section className="section-panel inline-form">
      <p className="section-code">Record outcome</p>
      <h2>Reconcile evidence to movement</h2>
      <label>
        Opportunity
        <select
          value={opportunityId}
          onChange={(event) => setOpportunityId(event.target.value)}
        >
          {data.opportunities.map((item) => (
            <option key={item.id} value={item.id}>
              {item.companyName} — {item.title}
            </option>
          ))}
        </select>
      </label>
      <label>
        Outcome
        <select
          value={outcomeType}
          onChange={(event) => setOutcomeType(event.target.value)}
        >
          {[
            "reply",
            "meeting",
            "proposal",
            "invoice",
            "payment",
            "won",
            "lost",
            "nurture",
            "unsubscribe",
            "complaint",
            "dispute",
          ].map((item) => (
            <option key={item}>{item}</option>
          ))}
        </select>
      </label>
      <label>
        Amount (A$, if applicable)
        <input
          type="number"
          min="0"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </label>
      <label>
        Evidence reference
        <textarea
          rows={3}
          value={evidence}
          onChange={(event) => setEvidence(event.target.value)}
        />
      </label>
      <CommandButton
        workspaceId={data.workspace.id}
        command="outcome.record"
        input={{
          opportunityId,
          outcomeType,
          amountCents: amount ? Math.round(Number(amount) * 100) : 0,
          evidence,
          occurredAt: new Date().toISOString(),
        }}
      >
        Record {verified ? "verified" : "pending"} outcome
      </CommandButton>
      <small>
        {verified
          ? "Your role records this as verified evidence."
          : "An owner or independent auditor must verify this outcome before it contributes to the cash score."}
      </small>
    </section>
  );
}

function Reports({ data }: { data: WorkspaceConsole }) {
  const [reportType, setReportType] = useState("sprint_outcome");
  const canGenerate =
    data.session.platformAdmin ||
    ["owner", "operator", "auditor"].includes(data.role);
  return (
    <Page title="Reports" eyebrow="Deliverables and follow-through">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Report register</p>
            <h2>Evidence-bound outputs</h2>
          </div>
          {canGenerate && (
            <div className="compact-control">
              <label>
                Report type
                <select
                  value={reportType}
                  onChange={(event) => setReportType(event.target.value)}
                >
                  {[
                    ["opportunity_map", "Cash Opportunity Map"],
                    ["daily_brief", "Daily operating brief"],
                    ["exception_file", "Exception file"],
                    ["action_register", "Action register"],
                    ["sprint_outcome", "Sprint outcome"],
                    ["fourteen_day_queue", "Fourteen-day queue"],
                    ["export_bundle", "Workspace export bundle"],
                  ].map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <CommandButton
                workspaceId={data.workspace.id}
                command="report.request-generation"
                input={{ reportType }}
              >
                Generate fixed snapshot
              </CommandButton>
            </div>
          )}
        </header>
        {data.reports.length ? (
          data.reports.map((report) => (
            <article className="report-record" key={report.id}>
              <FileText size={19} />
              <div>
                <b>{report.title}</b>
                <p>{report.summary}</p>
                <small>
                  Version {report.version} · snapshot {report.snapshotHash} ·{" "}
                  {date(report.finalisedAt || report.createdAt)}
                </small>
              </div>
              <div>
                <Status
                  tone={report.status === "delivered" ? "success" : "neutral"}
                >
                  {report.status}
                </Status>
                <a
                  className="action-button"
                  href={api.reportDownloadUrl(data.workspace.id, report.id)}
                  download
                >
                  Download JSON
                </a>
              </div>
            </article>
          ))
        ) : (
          <Empty title="No report is ready">
            Reports are generated from a fixed workspace snapshot and remain in
            review until their evidence and scope are confirmed.
          </Empty>
        )}
      </section>
    </Page>
  );
}

function Account({ data }: { data: WorkspaceConsole }) {
  const canAdminister = data.role === "owner" || data.session.platformAdmin;
  return (
    <Page
      title="Account and settings"
      eyebrow="Team, connections and retention"
    >
      <section className="three-column">
        <section className="section-panel">
          <p className="section-code">Team</p>
          <h2>Named access</h2>
          {data.members.length ? (
            data.members.map((member) => (
              <article className="record-line" key={member.userId}>
                <div>
                  <b>{member.displayName}</b>
                  <small>{member.email}</small>
                </div>
                <Status>{member.role}</Status>
              </article>
            ))
          ) : (
            <Empty title="No team member has been added">
              A workspace owner can ask Dreamers Revenue to issue a controlled
              invitation.
            </Empty>
          )}
          {canAdminister && (
            <CommandButton
              workspaceId={data.workspace.id}
              command="member.request-invitation"
            >
              Request team invitation
            </CommandButton>
          )}
        </section>
        <section className="section-panel">
          <p className="section-code">Connections</p>
          <h2>Provider readiness</h2>
          {data.integrations.map((integration) => (
            <article className="record-line" key={integration.id}>
              <div>
                <b>{integration.displayName}</b>
                <small>{integration.requiredFor}</small>
              </div>
              <Status
                tone={
                  integration.status === "connected"
                    ? "success"
                    : integration.status === "degraded"
                      ? "danger"
                      : "neutral"
                }
              >
                {integration.status}
              </Status>
            </article>
          ))}
          {!data.integrations.length && (
            <Empty title="No provider is connected">
              Connection setup is held until the owner supplies the appropriate
              account authority.
            </Empty>
          )}
        </section>
        <section className="section-panel">
          <p className="section-code">Retention</p>
          <h2>Exit and data rights</h2>
          <p>
            Export, return, archive, delete and access-revoke requests are
            queued for review. A legal hold prevents destructive action.
          </p>
          {canAdminister && (
            <>
              <CommandButton
                workspaceId={data.workspace.id}
                command="retention.request"
                input={{ requestType: "export" }}
              >
                Request export
              </CommandButton>
              <CommandButton
                workspaceId={data.workspace.id}
                command="retention.request"
                input={{ requestType: "revoke_access" }}
              >
                Request access revoke
              </CommandButton>
            </>
          )}
        </section>
      </section>
    </Page>
  );
}

function StaffApplications({ data }: { data: WorkspaceConsole }) {
  const [busy, setBusy] = useState("");
  const [message, setMessage] = useState("");
  const [links, setLinks] = useState("");
  const review = async (
    applicationId: string,
    decision: "qualified" | "declined" | "waiting_on_client",
  ) => {
    setBusy(applicationId);
    setMessage("");
    try {
      await api.reviewApplication(
        applicationId,
        decision,
        decision === "qualified"
          ? "Qualified for the controlled founding sprint."
          : decision === "declined"
            ? "Does not meet the current entry criteria."
            : "More authority or pipeline detail is required.",
        "sprint",
      );
      setMessage(
        "Application decision recorded. Refresh to load the new state.",
      );
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "Application review failed.",
      );
    } finally {
      setBusy("");
    }
  };
  const commercial = async (
    applicationId: string,
    invoiceReference: string,
  ) => {
    setBusy(applicationId);
    setMessage("");
    try {
      const result = await api.recordCommercial(applicationId, {
        paymentStatus: "paid",
        subtotalCents: 950000,
        gstCents: 95000,
        totalCents: 1045000,
        invoiceReference,
      });
      setLinks(
        [result.data.inviteUrl, result.data.intakeUrl]
          .filter(Boolean)
          .join("\n"),
      );
      setMessage(
        "Owner-recorded paid state saved. The returned links are single-purpose and must be transferred only to the named client.",
      );
    } catch (cause) {
      setMessage(
        cause instanceof Error ? cause.message : "Commercial state failed.",
      );
    } finally {
      setBusy("");
    }
  };
  return (
    <Page
      title="Applications and commercial"
      eyebrow="Owned new-business queue"
    >
      {message && <p className="inline-notice">{message}</p>}
      {links && <pre className="link-receipt">{links}</pre>}
      <section className="section-panel">
        {data.applications.length ? (
          data.applications.map((item) => (
            <article className="application-record" key={item.id}>
              <div>
                <p className="section-code">{item.reference}</p>
                <h2>{item.companyName}</h2>
                <p>
                  {item.contactName} · {item.contactEmail}
                </p>
                <blockquote>{item.pipelineSummary}</blockquote>
                <small>
                  {item.recordCount} records · {money(item.averageDealCents)}{" "}
                  average deal · received {date(item.createdAt)}
                </small>
              </div>
              <div>
                <Status
                  tone={
                    item.status === "qualified"
                      ? "success"
                      : item.status === "declined"
                        ? "danger"
                        : "neutral"
                  }
                >
                  {item.status.replaceAll("_", " ")}
                </Status>
                {!item.workspaceId && !["declined"].includes(item.status) && (
                  <div className="decision-actions">
                    <button
                      disabled={busy === item.id}
                      onClick={() => review(item.id, "qualified")}
                    >
                      Qualify
                    </button>
                    <button
                      disabled={busy === item.id}
                      onClick={() => review(item.id, "waiting_on_client")}
                    >
                      Request detail
                    </button>
                    <button
                      disabled={busy === item.id}
                      onClick={() => review(item.id, "declined")}
                    >
                      Decline
                    </button>
                  </div>
                )}
                {item.workspaceId && item.paymentStatus !== "paid" && (
                  <InvoiceGate
                    applicationId={item.id}
                    busy={busy === item.id}
                    onRecord={commercial}
                  />
                )}{" "}
                {item.workspaceId && (
                  <button
                    className="quiet-button"
                    onClick={() => go(`staff/workspace/${item.workspaceId}`)}
                  >
                    Open workspace
                  </button>
                )}
              </div>
            </article>
          ))
        ) : (
          <Empty title="No application is waiting">
            A public application appears here with its source record and
            receipt.
          </Empty>
        )}
      </section>
    </Page>
  );
}

function InvoiceGate({
  applicationId,
  busy,
  onRecord,
}: {
  applicationId: string;
  busy: boolean;
  onRecord: (applicationId: string, invoiceReference: string) => void;
}) {
  const [reference, setReference] = useState("");
  return (
    <div className="invoice-gate">
      <label>
        Dreamers Media invoice reference
        <input
          value={reference}
          onChange={(event) => setReference(event.target.value)}
          placeholder="Invoice reference"
        />
      </label>
      <button
        disabled={busy || !reference.trim()}
        onClick={() => onRecord(applicationId, reference.trim())}
      >
        Record paid + issue access
      </button>
      <small>This is owner-recorded, not provider-verified payment.</small>
    </div>
  );
}

function StaffOnboarding({ data }: { data: WorkspaceConsole }) {
  return (
    <Page title="Tenant workspace" eyebrow="Readiness and delivery clock">
      <section className="two-column">
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Input acceptance</p>
              <h2>Operator quality gate</h2>
            </div>
          </header>
          {data.onboarding.map((item) => (
            <article className="record-line" key={item.id}>
              <div>
                <b>{item.title}</b>
                <small>
                  {item.ownerRole} · {item.status.replaceAll("_", " ")}
                </small>
              </div>
              {item.status === "submitted" ? (
                <CommandButton
                  workspaceId={data.workspace.id}
                  command="onboarding.accept"
                  input={{ onboardingItemId: item.id }}
                >
                  Accept input
                </CommandButton>
              ) : (
                <Status>{item.status.replaceAll("_", " ")}</Status>
              )}
            </article>
          ))}
        </section>
        <section className="section-panel">
          <p className="section-code">Commercial documents</p>
          <h2>Versioned agreement register</h2>
          {data.agreements.length ? (
            data.agreements.map((agreement) => (
              <article className="record-line" key={agreement.id}>
                <div>
                  <b>{agreement.title}</b>
                  <small>
                    v{agreement.version} · review{" "}
                    {agreement.professionalReviewStatus}
                  </small>
                </div>
                <Status>{agreement.status}</Status>
              </article>
            ))
          ) : (
            <Empty title="No agreement draft is attached">
              Prepare the order form, service schedule and data schedule before
              onboarding can complete.
            </Empty>
          )}
          <CommandButton
            workspaceId={data.workspace.id}
            command="agreement.prepare"
          >
            Prepare operational drafts
          </CommandButton>
          <CommandButton
            workspaceId={data.workspace.id}
            command="engagement.start"
          >
            Start 72-hour clock
          </CommandButton>
          <Blocked reason="The clock remains blocked until payment, professional review, three accepted agreements and every required input are recorded." />
        </section>
      </section>
    </Page>
  );
}

function AuditDocket({ data }: { data: WorkspaceConsole }) {
  const reviewable = data.actions.filter((action) =>
    ["draft", "audit_failed", "audited"].includes(action.state),
  );
  return (
    <Page title="Auditor docket" eyebrow="Independent evidence gate">
      <section className="section-panel">
        {reviewable.length ? (
          reviewable.map((action) => (
            <article className="decision-record" key={action.id}>
              <div>
                <Status
                  tone={action.state === "audit_failed" ? "danger" : "neutral"}
                >
                  {action.state}
                </Status>
                <h3>{action.subject || action.channel}</h3>
                <p>
                  {action.companyName} · {action.recipient}
                </p>
                <blockquote>{action.body}</blockquote>
                <small>
                  Immutable content hash:{" "}
                  {action.contentHash || "missing — return to maker"}
                </small>
              </div>
              <div className="decision-actions">
                {["draft", "audit_failed"].includes(action.state) && (
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="action.submit-audit"
                    input={{ actionId: action.id }}
                  >
                    Pass independent audit
                  </CommandButton>
                )}
                <CommandButton
                  workspaceId={data.workspace.id}
                  command="action.request-change"
                  input={{ actionId: action.id }}
                >
                  Return for change
                </CommandButton>
              </div>
            </article>
          ))
        ) : (
          <Empty title="No independent review is waiting">
            Only source-bound drafts with a distinct maker enter this docket.
          </Empty>
        )}
      </section>
    </Page>
  );
}

function StaffView({ route, data }: { route: Route; data: WorkspaceConsole }) {
  const page = route.page;
  if (page === "applications") return <StaffApplications data={data} />;
  if (page === "workspace") return <StaffOnboarding data={data} />;
  if (page === "intake") return <Records data={data} />;
  if (page === "queue") return <Queue data={data} />;
  if (page === "drafts") return <Maker data={data} />;
  if (page === "audit") return <AuditDocket data={data} />;
  if (page === "communications") return <Communications data={data} />;
  if (page === "jobs") return <Jobs data={data} />;
  if (page === "reports") return <Reports data={data} />;
  if (page === "templates") return <Templates data={data} />;
  if (page === "integrations") return <Account data={data} />;
  if (page === "audit-log") return <AuditLog data={data} />;
  if (page === "settings") return <Account data={data} />;
  return <Portfolio data={data} />;
}
function Portfolio({ data }: { data: WorkspaceConsole }) {
  return (
    <Page title="Portfolio" eyebrow="Operator command register">
      <section className="ledger-strip">
        <article>
          <span>Open work</span>
          <strong>
            {data.tasks.filter((item) => item.status !== "completed").length}
          </strong>
          <small>Assigned and unassigned work</small>
        </article>
        <article>
          <span>Exceptions</span>
          <strong>
            {
              data.opportunities.filter((item) => item.stage === "quarantined")
                .length
            }
          </strong>
          <small>Records held from drafting</small>
        </article>
        <article>
          <span>Job runs</span>
          <strong>
            {
              data.jobs.filter((item) =>
                ["queued", "claimed"].includes(item.status),
              ).length
            }
          </strong>
          <small>Currently non-terminal</small>
        </article>
      </section>
      <Queue data={data} />
    </Page>
  );
}
function Queue({ data }: { data: WorkspaceConsole }) {
  const tasks = data.tasks.filter((task) => task.status !== "completed");
  return (
    <section className="section-panel">
      <header>
        <div>
          <p className="section-code">Assigned work</p>
          <h2>Operator queue</h2>
        </div>
        <CommandButton
          workspaceId={data.workspace.id}
          command="task.claim-next"
        >
          Claim next eligible task
        </CommandButton>
      </header>
      {tasks.length ? (
        tasks.map((task) => (
          <article className="work-record" key={task.id}>
            <Status tone={task.priority === "critical" ? "danger" : "neutral"}>
              {task.priority}
            </Status>
            <div>
              <b>{task.title}</b>
              <small>
                Owner: {task.ownerRole} · Due {date(task.dueAt)}
              </small>
              {task.blockedReason && <p>{task.blockedReason}</p>}
            </div>
            <Status>{task.status.replaceAll("_", " ")}</Status>
            <CommandButton
              workspaceId={data.workspace.id}
              command="task.start"
              input={{ taskId: task.id }}
            >
              Start
            </CommandButton>
          </article>
        ))
      ) : (
        <Empty title="No operator work is assigned">
          New work appears only after a controlled intake, exception or approval
          transition creates it.
        </Empty>
      )}
    </section>
  );
}
function Maker({ data }: { data: WorkspaceConsole }) {
  return (
    <Page title="Maker drafting" eyebrow="Source-bound commercial preparation">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Draft queue</p>
            <h2>Prepare, do not release.</h2>
          </div>
        </header>
        {data.actions.length ? (
          data.actions
            .filter((action) =>
              ["draft", "audit_failed"].includes(action.state),
            )
            .map((action) => (
              <article className="decision-record" key={action.id}>
                <div>
                  <h3>{action.subject || action.channel}</h3>
                  <p>
                    {action.companyName} · {action.recipient}
                  </p>
                  <blockquote>{action.body}</blockquote>
                </div>
                <div className="decision-actions">
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="action.request-audit"
                    input={{ actionId: action.id }}
                  >
                    Submit to audit queue
                  </CommandButton>
                  <CommandButton
                    workspaceId={data.workspace.id}
                    command="action.escalate"
                    input={{ actionId: action.id }}
                  >
                    Escalate uncertainty
                  </CommandButton>
                </div>
              </article>
            ))
        ) : (
          <Empty title="No draft is assigned">
            Drafting begins only from an eligible opportunity with source and
            suppression evidence.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function Jobs({ data }: { data: WorkspaceConsole }) {
  return (
    <Page title="Job runs" eyebrow="Runner health and recovery">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Run register</p>
            <h2>Every attempt is attributable.</h2>
          </div>
        </header>
        {data.jobs.length ? (
          data.jobs.map((job) => (
            <article className="job-record" key={job.id}>
              <div>
                <b>{job.kind}</b>
                <small>
                  {job.id} · attempt {job.attemptCount}/{job.maxAttempts} ·
                  heartbeat {date(job.heartbeatAt)}
                </small>
                {data.jobAttempts
                  .filter((attempt) => attempt.jobId === job.id)
                  .map((attempt) => (
                    <small key={attempt.id}>
                      Run {attempt.attemptNumber} · {attempt.runnerId} ·{" "}
                      {attempt.status} · lease {date(attempt.leaseExpiresAt)}
                      {attempt.failureCode ? ` · ${attempt.failureCode}` : ""}
                    </small>
                  ))}
                {job.terminalReason && <p>{job.terminalReason}</p>}
              </div>
              <Status
                tone={
                  job.status === "failed"
                    ? "danger"
                    : job.status === "completed"
                      ? "success"
                      : "neutral"
                }
              >
                {job.status}
              </Status>
              {["failed", "cancelled"].includes(job.status) ? (
                <CommandButton
                  workspaceId={data.workspace.id}
                  command="job.retry"
                  input={{ jobId: job.id }}
                >
                  Request retry
                </CommandButton>
              ) : job.status === "claimed" ? (
                <CommandButton
                  workspaceId={data.workspace.id}
                  command="job.recovery-check"
                  input={{ jobId: job.id }}
                >
                  Check lease
                </CommandButton>
              ) : (
                <CommandButton
                  workspaceId={data.workspace.id}
                  command="job.cancel"
                  input={{ jobId: job.id }}
                >
                  Request cancel
                </CommandButton>
              )}
            </article>
          ))
        ) : (
          <Empty title="No job run has been queued">
            The runner only receives bounded work after a role-authorised action
            creates it.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function Templates({ data }: { data: WorkspaceConsole }) {
  return (
    <Page
      title="Templates"
      eyebrow="Versioned communication and operational copy"
    >
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Template library</p>
            <h2>Approved language only</h2>
          </div>
          <CommandButton
            workspaceId={data.workspace.id}
            command="template.request-draft"
          >
            Request template draft
          </CommandButton>
        </header>
        {data.templates.length ? (
          data.templates.map((template) => (
            <article className="record-line" key={template.id}>
              <div>
                <b>{template.name}</b>
                <small>
                  {template.key} · v{template.version} · {template.channel}
                </small>
              </div>
              <Status
                tone={template.status === "active" ? "success" : "neutral"}
              >
                {template.status}
              </Status>
            </article>
          ))
        ) : (
          <Empty title="No template is available">
            A template must be versioned, scoped and reviewed before it can be
            used in a client workspace.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function AuditLog({ data }: { data: WorkspaceConsole }) {
  return (
    <Page title="Audit log" eyebrow="Committed workspace record">
      <section className="section-panel">
        <header>
          <div>
            <p className="section-code">Event stream</p>
            <h2>What changed, by whom.</h2>
          </div>
        </header>
        {data.events.length ? (
          data.events.map((event) => (
            <article className="event-record" key={event.id}>
              <time>{date(event.createdAt)}</time>
              <div>
                <b>{event.summary}</b>
                <small>
                  {event.actorType} / {event.actorId} / {event.type}
                </small>
              </div>
            </article>
          ))
        ) : (
          <Empty title="No committed event is available">
            The audit register appears as actions are recorded. It is not a
            substitute for a source record.
          </Empty>
        )}
      </section>
    </Page>
  );
}

function AdminView({ route, data }: { route: Route; data: AdminConsole }) {
  const page = route.page;
  if (page === "tenants") return <AdminTenants data={data} />;
  if (page === "users") return <AdminUsers data={data} />;
  if (page === "jobs") return <AdminJobs data={data} />;
  if (page === "integrations") return <AdminIntegrations data={data} />;
  if (page === "templates") return <AdminTemplates data={data} />;
  if (page === "retention") return <AdminRetention data={data} />;
  if (page === "settings") return <AdminSettings data={data} />;
  return (
    <Page title="Control room" eyebrow="Platform administration">
      <section className="ledger-strip">
        <article>
          <span>Tenants</span>
          <strong>{data.summary.tenants}</strong>
          <small>{data.summary.activeEngagements} active engagements</small>
        </article>
        <article>
          <span>Job fleet</span>
          <strong>{data.summary.queuedJobs}</strong>
          <small>{data.summary.staleJobs} stale job(s)</small>
        </article>
        <article>
          <span>Held items</span>
          <strong>
            {data.summary.blockedNotifications + data.summary.pendingRetention}
          </strong>
          <small>Notifications and retention requests</small>
        </article>
      </section>
      <AdminTenants data={data} />
    </Page>
  );
}
function AdminTenants({ data }: { data: AdminConsole }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const create = async () => {
    setMessage("");
    try {
      const created = await api.adminCommand<{ workspaceId: string }>(
        "workspace.create",
        { name, clientContactEmail: email },
      );
      setMessage(
        `Workspace ${created.data.workspaceId} created in provisioning. Refresh to open it.`,
      );
      setName("");
      setEmail("");
    } catch (cause) {
      setMessage(
        cause instanceof Error
          ? cause.message
          : "Workspace could not be created.",
      );
    }
  };
  return (
    <Page title="Tenants" eyebrow="Workspace lifecycle">
      <section className="two-column">
        <section className="section-panel">
          <h2>Create a controlled workspace</h2>
          <label>
            Client or workspace name
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <label>
            Named client contact
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <button
            className="action-button"
            disabled={!name.trim()}
            onClick={create}
          >
            Create in provisioning
          </button>
          {message && <p className="inline-notice">{message}</p>}
        </section>
        <section className="section-panel">
          <header>
            <div>
              <p className="section-code">Directory</p>
              <h2>Workspace state</h2>
            </div>
          </header>
          {data.workspaces.map((workspace) => (
            <article className="record-line" key={workspace.id}>
              <div>
                <b>{workspace.name}</b>
                <small>
                  {workspace.slug} · created {date(workspace.createdAt)}
                </small>
              </div>
              <Status
                tone={
                  workspace.lifecycle === "active"
                    ? "success"
                    : workspace.lifecycle === "archived"
                      ? "danger"
                      : "neutral"
                }
              >
                {workspace.lifecycle}
              </Status>
              <button
                className="quiet-button"
                onClick={() => go(`staff/workspace/${workspace.id}`)}
              >
                Open
              </button>
              {workspace.id !== "dreamers-revenue" && (
                <span className="lifecycle-actions">
                  {workspace.lifecycle === "paused" ? (
                    <CommandButton
                      admin
                      command="workspace.lifecycle"
                      input={{ workspaceId: workspace.id, lifecycle: "active" }}
                    >
                      Resume
                    </CommandButton>
                  ) : !["completed", "archived"].includes(
                      workspace.lifecycle,
                    ) ? (
                    <CommandButton
                      admin
                      command="workspace.lifecycle"
                      input={{ workspaceId: workspace.id, lifecycle: "paused" }}
                    >
                      Pause
                    </CommandButton>
                  ) : null}
                  {workspace.lifecycle !== "archived" && (
                    <CommandButton
                      admin
                      command="workspace.lifecycle"
                      input={{
                        workspaceId: workspace.id,
                        lifecycle: "completed",
                      }}
                    >
                      Complete
                    </CommandButton>
                  )}
                  {workspace.lifecycle === "completed" && (
                    <CommandButton
                      admin
                      command="workspace.lifecycle"
                      input={{
                        workspaceId: workspace.id,
                        lifecycle: "archived",
                      }}
                    >
                      Archive
                    </CommandButton>
                  )}
                </span>
              )}
            </article>
          ))}
        </section>
      </section>
    </Page>
  );
}
function AdminUsers({ data }: { data: AdminConsole }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [workspaceId, setWorkspaceId] = useState(
    data.workspaces.find((item) => item.id !== "dreamers-revenue")?.id ||
      "dreamers-revenue",
  );
  const [role, setRole] = useState("viewer");
  const [message, setMessage] = useState("");
  const [inviteUrl, setInviteUrl] = useState("");
  const invite = async () => {
    setMessage("");
    setInviteUrl("");
    try {
      const created = await api.invite<{
        inviteUrl: string;
        expiresAt: string;
      }>({ email, displayName: displayName || undefined, workspaceId, role });
      setInviteUrl(created.data.inviteUrl);
      setMessage(
        `Invitation created. It expires ${date(created.data.expiresAt)} and is shown only in this receipt.`,
      );
    } catch (cause) {
      setMessage(cause instanceof Error ? cause.message : "Invitation failed.");
    }
  };
  return (
    <Page title="Users and invitations" eyebrow="Access lifecycle">
      <section className="two-column">
        <section className="section-panel">
          <h2>Issue named access</h2>
          <label>
            Display name
            <input
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </label>
          <label>
            Email
            <input
              value={email}
              type="email"
              onChange={(event) => setEmail(event.target.value)}
            />
          </label>
          <label>
            Workspace
            <select
              value={workspaceId}
              onChange={(event) => setWorkspaceId(event.target.value)}
            >
              {data.workspaces.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Role
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
            >
              {["owner", "operator", "maker", "auditor", "viewer"].map(
                (item) => (
                  <option key={item}>{item}</option>
                ),
              )}
            </select>
          </label>
          <button
            className="action-button"
            disabled={!email || !workspaceId}
            onClick={invite}
          >
            Issue controlled invitation
          </button>
          {message && <p className="inline-notice">{message}</p>}
          {inviteUrl && (
            <div className="link-receipt">
              <b>Single-use invitation</b>
              <code>{inviteUrl}</code>
              <button onClick={() => navigator.clipboard.writeText(inviteUrl)}>
                Copy invitation
              </button>
            </div>
          )}
          <h3>Pending invitation register</h3>
          {data.invitations.slice(0, 20).map((item) => (
            <article className="record-line" key={item.id}>
              <div>
                <b>{item.displayName}</b>
                <small>
                  {item.email} · {item.workspaceName} · expires{" "}
                  {date(item.expiresAt)}
                </small>
              </div>
              <Status>{item.status}</Status>
            </article>
          ))}
        </section>
        <section className="section-panel">
          <h2>Users</h2>
          {data.users.map((user) => (
            <article className="record-line" key={user.id}>
              <div>
                <b>{user.displayName}</b>
                <small>
                  {user.email} · {user.memberships.length} membership(s)
                </small>
              </div>
              <Status
                tone={
                  user.status === "active"
                    ? "success"
                    : user.status === "revoked"
                      ? "danger"
                      : "neutral"
                }
              >
                {user.status}
              </Status>
              {!user.platformAdmin && (
                <CommandButton
                  admin
                  command="user.revoke-access"
                  input={{ userId: user.id }}
                >
                  Revoke
                </CommandButton>
              )}
            </article>
          ))}
        </section>
      </section>
    </Page>
  );
}
function AdminJobs({ data }: { data: AdminConsole }) {
  return (
    <Page title="Job fleet" eyebrow="Cross-tenant runner health">
      <section className="section-panel">
        {data.jobs.length ? (
          data.jobs.map((job) => (
            <article className="job-record" key={job.id}>
              <div>
                <b>
                  {job.workspaceName} · {job.kind}
                </b>
                <small>
                  {job.id} · attempt {job.attemptCount}/{job.maxAttempts}
                </small>
              </div>
              <Status
                tone={
                  job.status === "failed"
                    ? "danger"
                    : job.status === "completed"
                      ? "success"
                      : "neutral"
                }
              >
                {job.status}
              </Status>
              <CommandButton
                admin
                command="job.recovery-check"
                input={{ jobId: job.id }}
              >
                Check recovery
              </CommandButton>
            </article>
          ))
        ) : (
          <Empty title="No fleet job is available">
            The platform records jobs only after a workspace authorises bounded
            work.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function AdminIntegrations({ data }: { data: AdminConsole }) {
  return (
    <Page title="Integration health" eyebrow="Provider readiness">
      <section className="section-panel">
        {data.integrations.length ? (
          data.integrations.map((item) => (
            <article className="record-line" key={item.id}>
              <div>
                <b>{item.displayName}</b>
                <small>
                  {item.category} · {item.requiredFor} · checked{" "}
                  {date(item.lastCheckedAt)}
                </small>
              </div>
              <Status
                tone={
                  item.status === "connected"
                    ? "success"
                    : item.status === "degraded"
                      ? "danger"
                      : "neutral"
                }
              >
                {item.status}
              </Status>
              <CommandButton
                admin
                command="integration.check"
                input={{ integrationId: item.id }}
              >
                Check
              </CommandButton>
            </article>
          ))
        ) : (
          <Empty title="No integration has been configured">
            Providers are intentionally disconnected until an owner supplies
            account authority and the provider-specific verification can run.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function AdminTemplates({ data }: { data: AdminConsole }) {
  return (
    <Page title="Platform templates" eyebrow="Shared approved language">
      <section className="section-panel">
        {data.templates.length ? (
          data.templates.map((template) => (
            <article className="record-line" key={template.id}>
              <div>
                <b>{template.name}</b>
                <small>
                  {template.key} · v{template.version}
                </small>
              </div>
              <Status>{template.status}</Status>
            </article>
          ))
        ) : (
          <Empty title="No platform template has been promoted">
            Draft templates remain in review until the appropriate professional
            status is recorded.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function AdminRetention({ data }: { data: AdminConsole }) {
  return (
    <Page title="Retention queue" eyebrow="Data rights and closeout">
      <section className="section-panel">
        {data.retentionRequests.length ? (
          data.retentionRequests.map((request) => (
            <article className="record-line" key={request.id}>
              <div>
                <b>
                  {request.workspaceName} · {request.requestType}
                </b>
                <small>
                  {request.reason} · requested {date(request.createdAt)}
                </small>
              </div>
              <Status tone={request.legalHold ? "danger" : "neutral"}>
                {request.status}
              </Status>
              <CommandButton
                admin
                command="retention.review"
                input={{ retentionRequestId: request.id }}
              >
                Review
              </CommandButton>
            </article>
          ))
        ) : (
          <Empty title="No retention request is waiting">
            No workspace data is removed silently. Requests are recorded,
            reviewed and scheduled with legal-hold checks.
          </Empty>
        )}
      </section>
    </Page>
  );
}
function AdminSettings({ data }: { data: AdminConsole }) {
  return (
    <Page title="System controls" eyebrow="Fail-closed release policy">
      <section className="three-column">
        <section className="section-panel">
          <p className="section-code">External actions</p>
          <h2>{data.system.actionsEnabled ? "Enabled" : "Disabled"}</h2>
          <Blocked reason="Changing global release policy is owner-held and requires a separately reviewed connection." />
        </section>
        <section className="section-panel">
          <p className="section-code">Transactional email</p>
          <h2>{data.system.emailEnabled ? "Enabled" : "Disconnected"}</h2>
          <CommandButton admin command="system.email-readiness-check">
            Run readiness check
          </CommandButton>
        </section>
        <section className="section-panel">
          <p className="section-code">Runner</p>
          <h2>{data.system.runnerHealthy ? "Healthy" : "Needs review"}</h2>
          <CommandButton admin command="system.runner-health-check">
            Check runner
          </CommandButton>
        </section>
      </section>
    </Page>
  );
}

function Page({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow: string;
  children: ReactNode;
}) {
  return (
    <>
      <section className="page-heading">
        <p className="section-code">{eyebrow}</p>
        <h1>{title}</h1>
      </section>
      {children}
    </>
  );
}

function PrivateApp({ route }: { route: Route }) {
  const [workspace, setWorkspace] = useState<WorkspaceConsole | null>(null);
  const [admin, setAdmin] = useState<AdminConsole | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    setError("");
    try {
      if (route.area === "admin") setAdmin((await api.adminConsole()).data);
      else {
        const session = (await api.session()).data;
        const workspaceId =
          route.workspaceId || session.memberships[0]?.workspaceId;
        if (!workspaceId)
          throw new Error(
            "No workspace membership is available for this session.",
          );
        setWorkspace((await api.workspaceConsole(workspaceId)).data);
      }
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The private record could not be loaded.",
      );
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void refresh();
  }, [route.area, route.workspaceId]);
  if (loading)
    return (
      <main className="private-loading">
        <LoaderCircle className="spin" size={24} />
        <p>Loading the controlled record…</p>
      </main>
    );
  if (error)
    return (
      <main className="private-loading">
        <ErrorNotice message={error} retry={() => void refresh()} />
        <button className="primary-action" onClick={() => go("public/login")}>
          Return to access
        </button>
      </main>
    );
  if (route.area === "admin" && admin)
    return (
      <Shell route={route} admin={admin} refresh={refresh}>
        <AdminView route={route} data={admin} />
      </Shell>
    );
  if (!workspace) return null;
  const child =
    route.area === "staff" ? (
      <StaffView route={route} data={workspace} />
    ) : route.page === "onboarding" ? (
      <Onboarding data={workspace} />
    ) : route.page === "records" ? (
      <Records data={workspace} />
    ) : route.page === "opportunities" ? (
      <Opportunities data={workspace} />
    ) : route.page === "decisions" ? (
      <Decisions data={workspace} />
    ) : route.page === "communications" ? (
      <Communications data={workspace} />
    ) : route.page === "reports" ? (
      <Reports data={workspace} />
    ) : route.page === "account" ? (
      <Account data={workspace} />
    ) : (
      <Overview data={workspace} />
    );
  return (
    <Shell route={route} console={workspace} refresh={refresh}>
      {child}
    </Shell>
  );
}

function App() {
  const [route, setRoute] = useState(parseRoute());
  useEffect(() => {
    const handler = () => setRoute(parseRoute());
    addEventListener("hashchange", handler);
    return () => removeEventListener("hashchange", handler);
  }, []);
  const intakeToken = location.hash.startsWith("#intake/")
    ? decodeURIComponent(location.hash.slice(8))
    : "";
  const accessToken = location.hash.startsWith("#access/")
    ? decodeURIComponent(location.hash.slice(8))
    : "";
  if (intakeToken) return <TokenIntake token={intakeToken} />;
  if (accessToken) return <InvitationAccess token={accessToken} />;
  if (
    route.area === "client" ||
    route.area === "staff" ||
    route.area === "admin"
  )
    return <PrivateApp route={route} />;
  if (route.page === "apply") return <Application />;
  if (
    route.page === "privacy" ||
    route.page === "terms" ||
    route.page === "help"
  )
    return <LegalPage kind={route.page} />;
  if (route.page === "login") return <Login />;
  return <Landing />;
}

createRoot(document.getElementById("root")!).render(<App />);
